import _ from 'underscore';
import React from 'react';
import Joi from 'joi';
import getConfig from 'next/config';

import { api as apiRequest } from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';

import { getAdditionalBlocks } from '@culture/common-helpers/logic/blocks';
import Template from 'pageTemplates/watch';

const { serverRuntimeConfig } = getConfig();

const WatchPage = (props) => <Template {...props} />;

const getBlocks = (cleanCache) => {
	const blocks = {
		movies: {
			resource: 'movies',
			limit: 5,
			sort: '-views',
			params: addCleanCache(
				{
					fields: [
						'name',
						'title',
						'thumbnailFile',
						'type',
						'year',
						'duration',
						'institute'
					].join(),
					statuses: ['published'],
					types: ['movie'],
					cached: true
				},
				cleanCache
			)
		},
		performances: {
			resource: 'movies',
			limit: 5,
			sort: '-publishDate',
			params: addCleanCache(
				{
					fields: [
						'name',
						'title',
						'thumbnailFile',
						'type',
						'year',
						'duration',
						'institute'
					].join(),
					statuses: ['published'],
					types: ['performance'],
					cached: true
				},
				cleanCache
			)
		},
		concerts: {
			resource: 'movies',
			limit: 10,
			params: addCleanCache(
				{
					fields: [
						'name',
						'title',
						'thumbnailFile',
						'type',
						'institute'
					].join(),
					statuses: ['published'],
					types: ['concert'],
					cached: true
				},
				cleanCache
			)
		},
		lections: {
			resource: 'movies',
			limit: 10,
			params: addCleanCache(
				{
					fields: [
						'name',
						'title',
						'thumbnailFile',
						'type',
						'institute'
					].join(),
					statuses: ['published'],
					types: ['lecture'],
					excludeRubrics: [serverRuntimeConfig.entities.proLecturesRubric],
					cached: true
				},
				cleanCache
			)
		}
	};

	const mainCategories = ['cinema', 'theaters'];

	_(mainCategories).each((category) => {
		blocks[`${category}/persons`] = {
			resource: 'persons',
			limit: 10,
			params: {
				statuses: ['published'],
				sort: '-publishDate',
				rubrics: [serverRuntimeConfig.entities[`${category}PersonsRubric`]],
				fields: [
					'title',
					'rubrics',
					'country',
					'name',
					'thumbnailFile',
					'publishDate'
				].join(),
				cached: true
			}
		};
	});

	blocks.personRubrics = {
		resource: 'rubrics',
		limit: 20,
		params: addCleanCache(
			{
				paths: mainCategories,
				cached: true
			},
			cleanCache
		)
	};

	return blocks;
};

export async function getServerSideProps({ query }) {
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean().failover(false)
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const { text, blocks, textBlocks, rubric } = await apiRequest(
		'rubricPages/page',
		addCleanCache(
			{
				rubricPath: 'watch',
				blockFields: {
					searchPrompts: ['name', 'title', 'path'].join(),
					specialProjects: [
						'alias',
						'title',
						'description',
						'thumbnailFile'
					].join(),
					broadcast: [
						'name',
						'title',
						'type',
						'institute',
						'publishDate',
						'toDate',
						'thumbnailFile'
					].join(),
					promoBlocks: [
						'url',
						'title',
						'subTitle',
						'text',
						'thumbnailFile',
						'images'
					].join(),
					cinemaCompilations: ['title', 'shortText', 'mentionedEntities'].join()
				},
				completeBlocksParams: [
					{
						blockName: 'broadcast',
						blockParams: {
							statuses: ['published'],
							toDateStart: new Date(),
							limit: 10,
							sort: 'publishDate'
						}
					}
				],
				extendMixedByEntityType: true
			},
			params.cleanCache
		)
	);
	blocks.broadcast = blocks.broadcast.filter(
		(item) => Date.parse(item.toDate) > Date.now()
	);

	const blockData = getBlocks(params.cleanCache);

	const {
		movies,
		performances,
		...additionalBlocks
	} = await getAdditionalBlocks(blockData);

	additionalBlocks.filmsAndPerformances = [...movies, ...performances];

	if (!_(additionalBlocks.cinemaCompilations).isEmpty()) {
		additionalBlocks.cinemaCompilations = additionalBlocks.cinemaCompilations
			.filter((item) => !_(item.mentionedEntities?.movies).isEmpty())
			.slice(0, 2);
	}

	const counters = await apiRequest(
		'counters/entities',
		addCleanCache(
			{
				counters: [
					'movies',
					'performances',
					'concerts',
					'lectures',
					'broadcast'
				],
				cached: true
			},
			params.cleanCache
		)
	);

	return {
		props: {
			text,
			counters,
			textBlocks,
			blocks: { ...blocks, ...additionalBlocks },
			rubric
		}
	};
}

export default WatchPage;
